import React from 'react';
import { Box, Checkbox, CircularProgress, Stack, Table, Typography } from '@mui/joy';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import FolderIcon from '@mui/icons-material/Folder';
import FileRow from './components/FileRow';

const FileList = ({
  items = [],
  loading = false,
  isSelected,
  onSelectAll,
  allSelected,
  someSelected,
  onSelect,
  onOpen,
  onDownload,
  onContextMenu,
  renamingId,
  onRenameSubmit,
  onRenameCancel,
  dropTargetId,
}) => {
  if (loading && items.length === 0) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
        <CircularProgress size="lg" />
        <Typography level="body-sm" color="neutral" sx={{ mt: 2 }}>
          Loading files...
        </Typography>
      </Stack>
    );
  }

  if (!loading && items.length === 0) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          py: 8,
          border: '2px dashed',
          borderColor: 'neutral.300',
          borderRadius: 'lg',
          bgcolor: 'background.level1',
        }}
      >
        <FolderIcon sx={{ fontSize: 80, color: 'neutral.300' }} />
        <Typography level="h4" color="neutral" sx={{ mt: 2 }}>
          This folder is empty
        </Typography>
        <Typography level="body-sm" color="neutral" sx={{ mt: 1 }}>
          Drop files here or click Upload to get started
        </Typography>
      </Stack>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Droppable droppableId="storage-list" type="STORAGE_ITEM">
        {(provided, snapshot) => (
          <Table
            stickyHeader
            sx={{
              '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
              '--Table-headerUnderlineThickness': '1px',
              '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
              '& thead th': { fontWeight: 600, py: 1.5 },
              '& tbody': {
                bgcolor: snapshot.isDraggingOver ? 'primary.50' : 'transparent',
                transition: 'background-color 0.2s',
              },
            }}
          >
            <thead>
              <tr>
                <th style={{ width: 48, textAlign: 'center' }}>
                  <Checkbox
                    size="sm"
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    onChange={onSelectAll}
                  />
                </th>
                <th style={{ width: '45%' }}>Name</th>
                <th style={{ width: '15%' }}>Size</th>
                <th style={{ width: '25%' }}>Modified</th>
                <th style={{ width: '10%', textAlign: 'right', paddingRight: 16 }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody ref={provided.innerRef} {...provided.droppableProps}>
              {items.map((item, index) => (
                <Draggable key={item.path} draggableId={item.path} index={index}>
                  {(dragProvided, dragSnapshot) => (
                    <FileRow
                      item={item}
                      isSelected={isSelected(item.path)}
                      onSelect={onSelect}
                      onOpen={onOpen}
                      onDownload={onDownload}
                      onContextMenu={onContextMenu}
                      isRenaming={renamingId === item.path}
                      onRenameSubmit={(newName) => onRenameSubmit(item.path, newName)}
                      onRenameCancel={onRenameCancel}
                      isDragging={dragSnapshot.isDragging}
                      isDropTarget={dropTargetId === item.path && item.type === 'folder'}
                      dragHandleProps={dragProvided.dragHandleProps}
                      draggableProps={dragProvided.draggableProps}
                      innerRef={dragProvided.innerRef}
                    />
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </tbody>
          </Table>
        )}
      </Droppable>
    </Box>
  );
};

export default FileList;
